'use client';

import { useCallback, useEffect, useState } from 'react';
import { useCountdown } from '@/service/hooks/useCountdown';
import { Modal } from '@/ui/common/Modal/Modal';
import { FormButton } from '@/ui/common/Buttons/FormButton';

type TCountdownTimerPopupProps = {
    timeLeft: number;
    onCountdownEnd?: () => void;
    popupOpeningTimeMinutes?: number[];
    withActionButtons?: boolean;
    closePopupAfterAction?: boolean;
    countdownIsExtended?: boolean;
    primaryButtonText?: string;
    secondaryButtonText?: string;
    onClickPrimaryButton?: () => void;
    onClickSecondaryButton?: () => void;
};

export const CountdownTimerPopup = ({
    timeLeft,
    onCountdownEnd,
    popupOpeningTimeMinutes = [],
    withActionButtons = false,
    closePopupAfterAction = false,
    countdownIsExtended = false,
    primaryButtonText = 'Ok',
    secondaryButtonText = 'Cancel',
    onClickPrimaryButton,
    onClickSecondaryButton,
}: TCountdownTimerPopupProps) => {
    const [popupOpen, setPopupOpen] = useState(false);
    const [minutesIndex, setMinutesIndex] = useState(0);

    const { countdown, hours, minutes, seconds } = useCountdown(timeLeft);

    useEffect(() => {
        if (countdown <= 0) {
            setPopupOpen(false);
            if (onCountdownEnd) onCountdownEnd();
        } else setPopupOpen(parseInt(minutes) < popupOpeningTimeMinutes[minutesIndex]);
    }, [countdown, minutes, minutesIndex, onCountdownEnd, popupOpeningTimeMinutes]);

    useEffect(() => {
        if (countdownIsExtended) setMinutesIndex(0);
    }, [countdownIsExtended]);

    const handlePopupClose = useCallback(() => {
        setPopupOpen(false);
        setMinutesIndex((pS) => pS + 1);
    }, []);

    const handlePrimaryButtonClick = useCallback(() => {
        if (onClickPrimaryButton) {
            onClickPrimaryButton();
            if (closePopupAfterAction) handlePopupClose();
        }
    }, [closePopupAfterAction, handlePopupClose, onClickPrimaryButton]);

    const handleSecondaryButtonClick = useCallback(() => {
        if (onClickSecondaryButton) {
            onClickSecondaryButton();
            if (closePopupAfterAction) handlePopupClose();
        }
    }, [closePopupAfterAction, handlePopupClose, onClickSecondaryButton]);

    return (
        <Modal isOpen={popupOpen} closeModal={handlePopupClose}>
            <div data-test-id="expired-popup" className="flex w-full flex-col items-center">
                <div className="pointer-events-none flex h-[85px] w-[85px] items-center justify-center rounded-full bg-ash dark:bg-blue-1">
                    <div className="h-[55px] w-[30px] bg-sand-clock bg-contain bg-no-repeat dark:bg-sand-clock-dark" />
                </div>
                <div className="mt-[30px] flex w-full flex-col items-center gap-6">
                    <span className="tx-large-medium text-gray-1 dark:text-white">
                        Your tickets are being held for:
                    </span>
                    <div className="flex items-center justify-center gap-3">
                        <div className="flex flex-col items-center gap-2">
                            <div className="flex h-[60px] w-[61px] items-center justify-center rounded-[15px] bg-ash dark:bg-blue-1">
                                <h3
                                    data-test-id="expired-hours"
                                    className="tx-heading-3 text-gray-2 dark:text-gray-3"
                                >
                                    {hours}
                                </h3>
                            </div>
                            <span className="tx-large-regular text-gray-2 dark:text-white">
                                hours
                            </span>
                        </div>
                        <h3 className="tx-heading-3 mb-7 text-gray-2 dark:text-white">:</h3>
                        <div className="flex flex-col items-center gap-2">
                            <div className="flex h-[60px] w-[61px] items-center justify-center rounded-[15px] bg-ash dark:bg-blue-1">
                                <h3
                                    data-test-id="expired-mins"
                                    className="tx-heading-3 text-gray-2 dark:text-gray-3"
                                >
                                    {minutes}
                                </h3>
                            </div>
                            <span className="tx-large-regular text-gray-2 dark:text-white">
                                minutes
                            </span>
                        </div>
                        <h3 className="tx-heading-3 mb-7 text-gray-2 dark:text-white">:</h3>
                        <div className="flex flex-col items-center gap-2">
                            <div className="flex h-[60px] w-[61px] items-center justify-center rounded-[15px] bg-ash dark:bg-blue-1">
                                <h3
                                    data-test-id="expired-seconds"
                                    className="tx-heading-3 text-gray-2 dark:text-gray-3"
                                >
                                    {seconds}
                                </h3>
                            </div>
                            <span className="tx-large-regular text-gray-2 dark:text-white">
                                seconds
                            </span>
                        </div>
                    </div>
                </div>
                {withActionButtons && (
                    <div className="mt-[25px] flex w-full flex-col-reverse items-center justify-center gap-[17px] md:flex-row md:gap-5 lg:gap-[23px]">
                        <FormButton
                            type="button"
                            variant="outlined"
                            className="mx-0 h-[50px] max-w-[280px] px-0 text-blue-2 md:h-[55px] md:max-w-[270px] lg:h-[50px]"
                            label={primaryButtonText}
                            onClick={handlePrimaryButtonClick}
                            data-test-id="expired-checkout-now"
                        />
                        <FormButton
                            type="button"
                            className="mx-0 h-[50px] max-w-[280px] px-0 md:h-[55px] md:max-w-[270px] lg:h-[50px]"
                            label={secondaryButtonText}
                            onClick={handleSecondaryButtonClick}
                            data-test-id="extend-5-mins"
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};
